import React, { useState, useEffect, useRef } from "react";
import {
  FormControl,
  FormControlLabel,
  Box,
  RadioGroup,
  Radio,
  Button,
  Tooltip,
  Modal,
  Fade,
  Backdrop,
  IconButton,
  useMediaQuery
} from "@material-ui/core";
import { Check, Clear, ArrowBackIos, Visibility, ZoomIn, ZoomOut } from "@material-ui/icons";
import ExamReviewDrawer from "../exam-review-drawer/exam-review-drawer";
import MoreLikeThisPanel from "../more-like-this-panel/more-like-this-panel";
import AudioListenPanel from "../audio-listen-panel/audio-listen-panel";
import GlCardReviewDrawer from "../gl-card-review/gl-card-review-drawer";
import GlWhyCapture from "../gl-why-capture/gl-why-capture";
import { REPLAY_MASK } from "../../utils/replay-mask";
// Importing gl-service registers the production GL event sink (dormant when GL
// is off) and provides the real hint transport. WHY rides the card-draft call
// at grade time; "assisted" is recorded server-side at hint-serve (wire
// contract — the /gl/why + /gl/assisted callers were removed).
import { glApi as glProdApi, getUserIdHash } from "../gl-why-capture/gl-service";
import SessionFiducial from "../session-fiducial/session-fiducial";
import { SessionIdBook, normalizeFiducialQuestionId } from "../../utils/session-fiducial/session-fiducial";
import { createIdBookEmitter, loadSessionIdBook } from "../../utils/session-fiducial/id-book-emitter";
import { TokenAnnotator } from '../../vendor/react-text-annotate';
import { CONSTANTS } from "../../constants/app-constant";
import { resolveQlinkExamLabel } from "../../utils/qlink-exam-seed";
import { subscribeToSessionCleared } from "../../utils/stale-session";
import { api } from "../../services/apiService";
import {
  buildQLinkQuestionAnsweredBackendPayload,
  buildQLinkQuestionAnsweredMarkerKey,
  trackQLinkQuestionAnswered
} from "../../utils/tracking";
import "./exam-question-container.scss";

const pdfQuesData = [{
  page: 10,
  text: 'transfusion'
}, {
  page: 11,
  text: 'Circulatory changes at birth'
}];
let hasAnswered = [];

// Modal phone sheet: make everything outside the drawer inert (not focusable, not
// in the accessibility tree) by marking the siblings along the drawer's ancestor
// path up to <body>. Returns a restore function that removes only what it added.
const inertOutside = (element) => {
  const marked = [];
  let node = element;
  while (node && node.parentElement && node !== document.body) {
    Array.from(node.parentElement.children).forEach((sibling) => {
      if (sibling === node || sibling.hasAttribute("inert") || sibling.tagName === "SCRIPT") return;
      marked.push([sibling, sibling.getAttribute("aria-hidden")]);
      sibling.setAttribute("inert", "");
      sibling.setAttribute("aria-hidden", "true");
    });
    node = node.parentElement;
  }
  // Restore exactly what was there before (a pre-existing aria-hidden stays).
  return () => marked.forEach(([sibling, priorAriaHidden]) => {
    sibling.removeAttribute("inert");
    if (priorAriaHidden === null) sibling.removeAttribute("aria-hidden");
    else sibling.setAttribute("aria-hidden", priorAriaHidden);
  });
};

// The same raw grammar as the server and the loader:
// https://cdn.medlearnity.com/uploads/<[A-Za-z0-9._-]+>, no "..", no port.
const TEASER_THUMBNAIL_URL = /^https:\/\/cdn\.medlearnity\.com\/uploads\/[A-Za-z0-9._-]+$/;
const allowedTeaserThumbnail = (value) => (
  typeof value === "string" && value.length <= 500 && TEASER_THUMBNAIL_URL.test(value) && !value.includes("..")
    ? value
    : null
);

// Shape-check the optional teaser from the public answer check; anything
// unexpected is dropped and the visitor gets today's sign-in wall.
const sanitizePublicTeaser = (raw) => {
  if (!raw || typeof raw !== "object" || typeof raw.firstSentence !== "string" || !raw.firstSentence.trim()) {
    return null;
  }
  const offer = raw.offer && typeof raw.offer === "object"
    && Number.isInteger(raw.offer.dailyQuestions) && raw.offer.dailyQuestions > 0
    && typeof raw.offer.examType === "string" && raw.offer.examType.trim()
    ? { dailyQuestions: raw.offer.dailyQuestions, examType: raw.offer.examType.trim() }
    : null;
  return {
    firstSentence: raw.firstSentence.trim(),
    hook: typeof raw.hook === "string" && raw.hook.trim() ? raw.hook.trim() : null,
    sections: Array.isArray(raw.sections)
      ? raw.sections.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim()).slice(0, 8)
      : [],
    figureThumbnailUrl: allowedTeaserThumbnail(raw.figureThumbnailUrl),
    offer,
  };
};

const ExamQuestionContainer = (props) => {
  const {
    currentQuestionNum,
    questionData,
    dataLength,
    onProceedToNextQuestion,
    isReview,
    readOnly = false,
    isTutor,
    isGuide,
    examHistoryData,
    onExamHistoryChange,
    questionMetadata,
    quizHistoryData,
    onShowEndBlock,
    imageData,
    onFeedbackOpen,
    strikedHistoryData,
    onStrikeDataChange,
    isUserAcquisition,
    userDetailsFromTokenRes,
    handleFetchQuestionMetadata,
    questionMetadataError,
    questionMetadataLoading = false,
    questionMetadataLoaded = false,
    qlinkId,
    qlinkPlatform,
    quizId
  } = props;
  const [selectedAnswer, setSelectedAnswer] = useState("");
  // Server-graded results keyed by question id (in-quiz egress Slice B):
  // { correct, question } per QLearn-service #483, where question is the
  // REVEALED view (answer key + explanations). Held per-question so back-nav
  // re-renders feedback without a refetch.
  const [serverGrades, setServerGrades] = useState({});
  // R3 (P0 blank-explanation fix): a failed A2 grade used to be swallowed
  // silently (catch → delete pending, no UI), which is exactly what turned a
  // transient failure into a sticky BLANK Explanation tab. Track the failure
  // per question so the drawer can render an explicit unavailable/retry state.
  const [serverGradeErrors, setServerGradeErrors] = useState({});
  // Signed-out QLink answer check (first-experience contract): the page payload
  // carries no answer key, so the FIRST answer to a shared question asks the
  // server, scoped to this link's question. Keyed by question id:
  // { status: 'pending' | 'done' | 'error', correctAnswer }.
  const [publicChecks, setPublicChecks] = useState({});
  const requestedPublicChecksRef = useRef(new Set());
  const [quesMetadata, setQuesMetadata] = useState({});
  const [quizHistory, setQuizHistory] = useState({});
  const [pdfData] = useState(pdfQuesData[0]);
  const [openReviewDrawer, setOpenReviewDrawer] = useState(false);
  // Whether the CURRENT open was automatic. Set at each open site; only the
  // explicit toggle clears it.
  const drawerAutoOpenedRef = useRef(false);
  // QLink "Sign in free to see why" (see the per-question reset effect): the
  // question and sign-in state that effect last ran for, and the question the
  // visitor last answered while signed out on this page.
  const drawerResetRunRef = useRef(null);
  const answeredSignedOutRef = useRef("");
  const [hideAnswer, setHideAnswer] = useState(false);
  const [openPdf, setOpenPdf] = useState(false);
  const [isTutorRole, setIsTutorRole] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [highlightValues, setHighlightValues] = useState({});
  const [strikeValues, setStrikeValues] = useState([]);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  const imgRef = useRef(null);
  const drawerRef = useRef(null);
  const pendingServerGradeRef = useRef(new Set());
  // R3: guards the one-shot retry-on-open heal so it never loops per render.
  const explanationHealRef = useRef("");
  const trackedQLinkAnswerEventsRef = useRef(new Set());
  const postedQLinkAnswerEventsRef = useRef(new Set());
  const qlinkAnswerTrackingWarnedRef = useRef(false);
  // One id-book per tutoring session — maps this session's question-ids to the
  // small tag indices painted by the Zoom-watermark fiducial (see
  // utils/session-fiducial). Rehydrated from sessionStorage (same tab) so a
  // reload never re-assigns tag indices, and EMITTED to the backend id-book
  // store as questions are first painted — without the persisted book the
  // recorded tags decode to opaque indices with no way back to a question-id
  // (see utils/session-fiducial/id-book-emitter.js).
  const sessionIdBookRef = useRef(null);
  const idBookEmitterRef = useRef(null);
  if (sessionIdBookRef.current === null) {
    sessionIdBookRef.current = !readOnly && CONSTANTS.sessionFiducialEnabled
      ? loadSessionIdBook({
        search: window.location.search,
        storage: window.sessionStorage
      })
      : new SessionIdBook();
    idBookEmitterRef.current = createIdBookEmitter({
      idBook: sessionIdBookRef.current,
      postIdBook: api.upsertSessionFiducialIdBook,
      storage: window.sessionStorage
    });
  }

  const getQuestionId = () => questionData?.questionId || questionData?.id;
  const normalizeQuestionId = (value) => value === undefined || value === null ? "" : `${value}`;
  const getMetadataQuestionId = (metadata) => metadata?.id ?? metadata?.questionId;
  const isCurrentQuestionMetadata = (metadata) => {
    const currentQuestionId = normalizeQuestionId(getQuestionId());

    return currentQuestionId && normalizeQuestionId(getMetadataQuestionId(metadata)) === currentQuestionId;
  };

  const getSelectedAnswerKey = (answerValue) => {
    const normalizedAnswer = `${answerValue || ''}`.trim();
    const optionIndex = (questionData?.option?.optionList || []).findIndex(
      (item) => `${item || ''}`.trim() === normalizedAnswer
    );

    return optionIndex >= 0 ? `option_${optionIndex}` : 'option_unknown';
  };

  // --- Server-authoritative in-quiz grading (in-quiz egress Slice B, gated
  // design qlearn-unified #115; QLearn-service #483). DARK behind
  // CONSTANTS.serverGradingEnabled: with the flag off nothing below runs and
  // every existing path is byte-identical. Active only for a tutor-mode quiz
  // with a known quizId — review renders recorded history, and test/timed
  // quizzes grade once at submit (already server-side), so neither uses this.
  const serverGradingActive =
    CONSTANTS.serverGradingEnabled && !!isTutor && !isReview && !!quizId;
  const currentServerGrade = serverGradingActive
    ? serverGrades[normalizeQuestionId(getQuestionId())]
    : undefined;
  // Once a grade exists, the server's revealed question (key + explanations)
  // supersedes the fetched metadata; until then (pending/failed/flag-off) the
  // fetched metadata keeps today's behavior. Fail-closed: no grade + a
  // key-stripped payload (Slice C) simply renders no ✓/✗ — the same guard the
  // anonymous QLink path uses.
  // MERGE the reveal over the batch item — never replace (#528 port): the
  // reveal DTO (QuestionViewResponse: id, stem, option, explanation,
  // briefExplanation, additionalExplanation) is NOT a superset of the authed
  // batch metadata (the full Question doc — videoLinks, refLinks, categories,
  // medicalCategories, imageList...), so a wholesale replace silently drops
  // fields the guidance drawer renders. The reveal wins every conflict
  // (incl. option.correctAnswer); the batch item is used as the merge base
  // only when it already belongs to the CURRENT question, so a late-effect
  // navigation window can never blend a neighbor's fields into the reveal.
  const gradedReveal = currentServerGrade?.question;
  const revealMergeBase =
    gradedReveal && isCurrentQuestionMetadata(quesMetadata) ? quesMetadata : undefined;
  const serverEffectiveQuesMetadata = gradedReveal
    ? {
      ...(revealMergeBase || {}),
      ...gradedReveal,
      option: { ...(revealMergeBase?.option || {}), ...(gradedReveal.option || {}) }
    }
    : quesMetadata;
  // A done public check fills ONLY a missing key (never overrides one the
  // payload already carries), which lights up the existing per-option ✓/✗.
  const currentPublicCheck = publicChecks[normalizeQuestionId(getQuestionId())];
  const publicCheckKey = currentPublicCheck?.status === "done" ? currentPublicCheck.correctAnswer : "";
  const effectiveQuesMetadata = publicCheckKey && !serverEffectiveQuesMetadata?.option?.correctAnswer
    ? {
      ...serverEffectiveQuesMetadata,
      option: { ...serverEffectiveQuesMetadata?.option, correctAnswer: publicCheckKey }
    }
    : serverEffectiveQuesMetadata;

  // R3: the failure the P0 came from — a held/answered question with no
  // explanation to show. currentServerGradeError is set when the A2 grade POST
  // fails; isAnswered gates the drawer's explicit unavailable state (unanswered
  // questions stay stripped, no error state); effectiveHasExplanation says
  // whether the reveal actually carried guidance.
  const currentServerGradeError = serverGradingActive
    ? serverGradeErrors[normalizeQuestionId(getQuestionId())]
    : undefined;
  // Role-aware, mirroring the drawer's hasExplanationContent (QUI#610 gate
  // finding 1): the drawer renders `explanation` ONLY for admins, so for a
  // non-admin a record whose only guidance field is `explanation` carries no
  // visible content — counting it here would suppress the retry-on-open
  // metadata heal while the drawer shows the unavailable state.
  const isAdminRole = window.localStorage.getItem("role") === "ROLE_ADMIN";
  const effectiveHasExplanation = !!(
    effectiveQuesMetadata?.briefExplanation ||
    effectiveQuesMetadata?.additionalExplanation ||
    (isAdminRole && effectiveQuesMetadata?.explanation)
  );
  /*
   * The LOCAL answer record, which is what makes tutor mode observable.
   *
   * `quizHistory` is only populated when `isReview` (the effect that calls
   * setQuizHistory is gated on it), and `currentServerGrade` needs
   * serverGradingEnabled, which is dark. So `isAnswered` was false on the tutor
   * path — the default study mode — and `explanation_opened` never fired there.
   * The drawer auto-opens, the student reads the explanation, and nothing was
   * measured: the metric was review-mode-only while its own rationale for the
   * `autoOpened` dimension is that tutor-mode auto-opens "would saturate near
   * 100%". That population was never observed.
   *
   * examHistoryData is the client's own record of what the student answered on
   * this question, and it is already trusted three lines down for QLink
   * reporting.
   */
  const localAnswerRecord = examHistoryData?.[getQuestionId()];
  const isAnswered =
    !!currentServerGrade ||
    !!(quizHistory && (quizHistory.answerGiven != null || quizHistory.correct != null)) ||
    (serverGradingActive && !!selectedAnswer && !!currentServerGradeError);

  /*
   * A SEPARATE signal for the instrument, deliberately not folded into
   * `isAnswered`.
   *
   * The first version broadened `isAnswered` itself, which has two other
   * consumers: the explanation-heal effect (one extra metadata refetch per
   * question in tutor mode) and `showExplanationUnavailable` (the strip now
   * rendering where it never did). Both may be improvements, but they are a
   * user-visible and backend-load change on the DEFAULT study mode, and
   * shipping them inside an instrumentation PR — untested and unmentioned — is
   * not the trade this PR is asking for.
   */
  const hasAnsweredLocally =
    isAnswered ||
    localAnswerRecord?.answerGiven != null;

  // The review drawer's `answeredCorrectly`: the quiz history's verdict, else
  // the server grade's, else (the tutor path) derived from the local answer.
  const resolveAnsweredCorrectly = () => {
    if (typeof quizHistory?.correct === "boolean") return quizHistory.correct;
    if (typeof currentServerGrade?.correct === "boolean") return currentServerGrade.correct;
    // The tutor path, DERIVED. `localAnswerRecord.correct` was a
    // dead wire: nothing in this codebase ever writes `.correct`
    // onto examHistoryData — only `answerGiven` and `marked` — so
    // the curiosity-vs-repair cut, the entire reason this event
    // carries the field, was `undefined` for 100% of
    // default-study-mode reads. Round 2's defect, one layer down.
    //
    // getAnswerCorrectness derives it from
    // effectiveQuesMetadata.option.correctAnswer, which logged-in
    // callers receive, and is what the QLink path already trusts.
    return getAnswerCorrectness(localAnswerRecord?.answerGiven);
  };

  const submitAnswerForServerGrade = (answerValue) => {
    const questionKey = normalizeQuestionId(getQuestionId());
    if (
      !serverGradingActive ||
      !questionKey ||
      !answerValue ||
      serverGrades[questionKey] ||
      pendingServerGradeRef.current.has(questionKey)
    ) {
      return;
    }

    pendingServerGradeRef.current.add(questionKey);
    // The answer is FINAL server-side: a re-POST returns the first recorded
    // result and never re-grades, which is also why a locally re-picked option
    // is never re-submitted once a grade is held.
    api.submitQuestionAnswer(quizId, getQuestionId(), answerValue)
      .then((response) => {
        pendingServerGradeRef.current.delete(questionKey);
        if (response && !response.errStatus && typeof response.correct === "boolean") {
          setServerGrades((prev) => ({ ...prev, [questionKey]: response }));
          clearServerGradeError(questionKey);
        } else {
          // Non-200 (e.g. the A2 categoryMap 500): surface it instead of
          // swallowing — errStatus follows the #603 errStatus||status shape.
          setServerGradeError(questionKey, response?.errStatus || response?.status || true);
        }
      })
      .catch((err) => {
        pendingServerGradeRef.current.delete(questionKey);
        setServerGradeError(questionKey, err?.errStatus || err?.status || true);
      });
  };

  const requestPublicAnswerCheck = (answerValue) => {
    const questionId = getQuestionId();
    const questionKey = normalizeQuestionId(questionId);
    if (
      !isUserAcquisition ||
      isUserLoggedIn ||
      !qlinkId ||
      !questionKey ||
      !answerValue ||
      effectiveQuesMetadata?.option?.correctAnswer ||
      requestedPublicChecksRef.current.has(questionKey)
    ) {
      return;
    }
    if (typeof api.checkQLinkAnswer !== "function") {
      return;
    }
    requestedPublicChecksRef.current.add(questionKey);
    setPublicChecks((prev) => ({ ...prev, [questionKey]: { status: "pending" } }));
    const settle = (value) => setPublicChecks((prev) => ({ ...prev, [questionKey]: value }));
    // Answering must never fail because of the check: any throw settles to
    // "error", which renders today's sign-in wall unchanged.
    new Promise((resolve) => resolve(api.checkQLinkAnswer({
      qlinkId,
      questionId,
      answer: `${answerValue}`.trim(),
      platform: qlinkPlatform,
    })))
      .then((response) => {
        if (response && !response.errStatus && typeof response.correctAnswer === "string" && response.correctAnswer.trim()) {
          settle({
            status: "done",
            correctAnswer: response.correctAnswer.trim(),
            // Curated "sign in to see why" teaser: only ever arrives here, in the
            // answer-check response, so nothing answer-revealing is in the bundle.
            teaser: sanitizePublicTeaser(response.teaser),
            checkedAnswer: `${answerValue}`.trim(),
          });
        } else {
          settle({ status: "error" });
        }
      })
      .catch(() => settle({ status: "error" }));
  };

  const setServerGradeError = (questionKey, code) => {
    setServerGradeErrors((prev) => ({ ...prev, [questionKey]: code }));
  };

  const clearServerGradeError = (questionKey) => {
    setServerGradeErrors((prev) => {
      if (!(questionKey in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[questionKey];
      return next;
    });
  };

  // R3 retry semantics: recover an answered-but-unexplained question without
  // stranding it. Re-POST A2 when there is no held grade (finality: idempotent,
  // never re-grades) and force a metadata refetch when the reveal is empty.
  const retryExplanation = () => {
    const questionKey = normalizeQuestionId(getQuestionId());
    if (serverGradingActive && !currentServerGrade) {
      clearServerGradeError(questionKey);
      submitAnswerForServerGrade(selectedAnswer || quizHistory?.answerGiven);
    }
    if (typeof handleFetchQuestionMetadata === "function") {
      handleFetchQuestionMetadata();
    }
  };

  // Retry-on-open: when the guidance drawer opens for an answered question that
  // has no explanation yet, make ONE heal attempt (guarded once per question so
  // a re-render never loops; the in-flight/held-grade guards inside
  // submitAnswerForServerGrade keep it from double-POSTing during the initial
  // answer, when the drawer auto-opens while the first POST is still pending).
  useEffect(() => {
    if (!openReviewDrawer) {
      return;
    }
    const questionKey = normalizeQuestionId(getQuestionId());
    if (!questionKey || explanationHealRef.current === questionKey) {
      return;
    }
    explanationHealRef.current = questionKey;
    if (!isAnswered) {
      // Not answered yet → strip is by design; nothing to heal.
      explanationHealRef.current = "";
      return;
    }
    if (serverGradingActive && !currentServerGrade) {
      submitAnswerForServerGrade(selectedAnswer || quizHistory?.answerGiven);
    }
    if (!effectiveHasExplanation && typeof handleFetchQuestionMetadata === "function") {
      handleFetchQuestionMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReviewDrawer, questionData, currentQuestionNum]);

  const getAnswerCorrectness = (answerValue) => {
    // Slice B: a held server grade is authoritative — the server graded the
    // recorded FINAL answer, independent of whether the payload still carries
    // the key. QLink demo answers have no quiz to grade against and keep the
    // metadata path below.
    if (serverGradingActive && currentServerGrade && typeof currentServerGrade.correct === "boolean") {
      return currentServerGrade.correct;
    }

    const correctAnswer = effectiveQuesMetadata?.option?.correctAnswer;
    if (!correctAnswer || !answerValue) {
      return undefined;
    }

    return correctAnswer.trim().toLowerCase() === `${answerValue}`.trim().toLowerCase();
  };

  const recordQLinkQuestionAnswered = (answerValue) => {
    const questionId = getQuestionId();
    if (!isUserAcquisition || !qlinkId || !questionId || !answerValue) {
      return;
    }

    const selectedAnswerKey = getSelectedAnswerKey(answerValue);
    const markerKey = buildQLinkQuestionAnsweredMarkerKey({
      qlinkId,
      questionId,
      selectedAnswerKey
    });
    const userId = userDetailsFromTokenRes?.id || window.localStorage.getItem('userId');
    const eventParams = {
      qlinkId,
      questionId,
      userId,
      platform: qlinkPlatform,
      correct: getAnswerCorrectness(answerValue)
    };

    if (!trackedQLinkAnswerEventsRef.current.has(markerKey)) {
      trackQLinkQuestionAnswered(eventParams);
      trackedQLinkAnswerEventsRef.current.add(markerKey);
    }

    if (!isUserLoggedIn || !userId) {
      return;
    }

    // EXPLICIT SKIP under the in-quiz answer strip (C2 flip gate F1): with
    // the strip on, a QLink payload never carries option.correctAnswer — the
    // reveal fail-closes without a quiz context (QLS InQuizRevealService),
    // QLink answers write no quiz history, and the A2 grading endpoint needs
    // an owned quiz, so no post-answer refetch can restore the key.
    // Correctness is then not computable client-side and the backend
    // acquisition event is deliberately NOT posted rather than posted with a
    // bogus correct flag. The markerKey is NOT added to the posted set, so if
    // metadata later arrives WITH the key (strip off — the pre-strip late
    // batch fetch), the quesMetadata effect re-runs this and the event posts;
    // today's behavior is unchanged.
    if (typeof eventParams.correct !== 'boolean') {
      return;
    }

    if (postedQLinkAnswerEventsRef.current.has(markerKey)) {
      return;
    }

    postedQLinkAnswerEventsRef.current.add(markerKey);
    api.recordQLinkQuestionAnswered(
      buildQLinkQuestionAnsweredBackendPayload(eventParams)
    ).then((response) => {
      if (response?.errStatus) {
        postedQLinkAnswerEventsRef.current.delete(markerKey);
      }
    }).catch(() => {
      postedQLinkAnswerEventsRef.current.delete(markerKey);
    });
  };

  const safeRecordQLinkQuestionAnswered = (answerValue) => {
    try {
      recordQLinkQuestionAnswered(answerValue);
    } catch (_) {
      // Tracking should never block answering a QLink question.
      if (!qlinkAnswerTrackingWarnedRef.current && typeof console !== 'undefined' && console.warn) {
        qlinkAnswerTrackingWarnedRef.current = true;
        console.warn('QLink answer tracking failed.');
      }
    }
  };

  const setTutorRoleOnLoad = () => {
    const role = window.localStorage.getItem('role');
    setIsTutorRole(role === 'ROLE_TUTOR');

    if (role) {
      setIsUserLoggedIn(true);
    }
  }

  useEffect(() => {
    setTutorRoleOnLoad();

    if ((isReview)) {
      drawerAutoOpenedRef.current = true; // review mode opens the drawer on mount
      setOpenReviewDrawer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((userDetailsFromTokenRes?.status === 200 || userDetailsFromTokenRes?.id) && !isUserLoggedIn) {
      setIsUserLoggedIn(true);
      setTutorRoleOnLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsFromTokenRes]);

  // QLink: the login read at mount was revoked server-side (unexpired, so it
  // passed every client check) and the stale-session guard just cleared it
  // without navigating. The visitor is a stranger now: the signed-out answer
  // check and sign-in wall, not a signed-in drawer with nothing to load.
  useEffect(() => {
    if (!isUserAcquisition) {
      return undefined;
    }
    return subscribeToSessionCleared(() => {
      if (window.localStorage.getItem("token")) {
        return; // a newer sign-in won
      }
      setIsUserLoggedIn(false);
      setIsTutorRole(false);
    });
  }, [isUserAcquisition]);

  // Zoom-watermark id-book EMISSION: whenever the fiducial is active for the
  // current question, register the question-id (idempotent — SessionFiducial
  // registers on render too) and persist/emit the full cumulative book so the
  // recording decoder can map the painted tag index back to this question.
  // A failed emission self-retries and is also healed by the next navigation
  // (every emission carries the whole map).
  const fiducialActive = !readOnly && CONSTANTS.sessionFiducialEnabled && (isTutor || isTutorRole);
  const fiducialQuestionId = fiducialActive
    ? normalizeFiducialQuestionId(getQuestionId())
    : "";

  useEffect(() => {
    if (!fiducialActive || !fiducialQuestionId) {
      return;
    }
    sessionIdBookRef.current.register(fiducialQuestionId);
    idBookEmitterRef.current.sync();
  }, [fiducialActive, fiducialQuestionId]);

  useEffect(() => () => idBookEmitterRef.current.dispose(), []);

  useEffect(() => {
    hasAnswered = questionMetadata.filter((item) => examHistoryData[item.id]?.answerGiven);

    if (!isReview) {
      setSelectedAnswer(examHistoryData[questionData?.questionId]?.answerGiven);
    }

    // QLink "Sign in free to see why": the visitor answered this question
    // signed out, signs in from the drawer (Google popup or Phone) and stays
    // here. The isUserLoggedIn flip, and the guidance refetch that then hands
    // in a new questionData object for the same question, both re-run this
    // effect; closing on them hid the explanation they had just signed in for.
    // So, on the SAME answered question: the flip opens the drawer if the
    // answer was given signed out on this page (a session merely restored on
    // mount is an arrival, not a sign-in), and a later re-run leaves the
    // drawer, and whether its open was deliberate, as the visitor has it.
    const questionKey = normalizeQuestionId(getQuestionId());
    const previousRun = drawerResetRunRef.current;
    drawerResetRunRef.current = { questionKey, isUserLoggedIn };
    const qlinkSameAnsweredQuestion = !!isUserAcquisition && isUserLoggedIn && !!questionKey
      && previousRun?.questionKey === questionKey
      && !!(examHistoryData?.[getQuestionId()]?.answerGiven || selectedAnswer);
    const qlinkSignedInHere = qlinkSameAnsweredQuestion && !previousRun.isUserLoggedIn
      && answeredSignedOutRef.current === questionKey;
    const qlinkSameQuestionRerun = qlinkSameAnsweredQuestion && previousRun.isUserLoggedIn;
    // Signed out on the SAME question, the drawer stays as the visitor has it
    // (open on the result and sign-in wall, or closed by them) in two cases:
    // - the reverse flip (the session-cleared subscription above): a dead
    //   login was just cleared on this question. An answer already given gets
    //   the public check it skipped while signed in, and counts as answered
    //   signed out, so signing in opens its explanation;
    // - a later re-run for a question answered signed out, such as the public
    //   metadata landing after the answer (a new questionData object).
    const qlinkSameQuestionSignedOut = !!isUserAcquisition && !isUserLoggedIn && !!questionKey
      && previousRun?.questionKey === questionKey;
    const qlinkSignedOutHere = qlinkSameQuestionSignedOut && !!previousRun.isUserLoggedIn;
    const qlinkKeepDrawerSignedOut = qlinkSameQuestionSignedOut
      && (qlinkSignedOutHere || answeredSignedOutRef.current === questionKey);

    // Re-arm per question. Review mode intentionally keeps the drawer open
    // across questions, so without this the flag is stale for every question
    // after the first: one incidental click on page chrome (handleContainerClick
    // toggles on any non-control click) would mark all 39 remaining questions in
    // a block as deliberate reads — inflating the exact numerator the metric
    // exists to produce, and indistinguishable afterwards from a real one.
    // Defaults to automatic, which is the conservative direction: an open the
    // student did not perform this question is not a read. Skipped on a QLink
    // same-question re-run: that keeps the current open, so it keeps that
    // open's attribution too. The sign-in open itself is re-armed (automatic).
    if (!qlinkSameQuestionRerun && !qlinkKeepDrawerSignedOut) {
      drawerAutoOpenedRef.current = true;
    }

    if ((isUserAcquisition && !isUserLoggedIn && !qlinkKeepDrawerSignedOut) || (isTutor && !isReview)) {
      setOpenReviewDrawer(false);
    }

    if (qlinkSignedOutHere) {
      const answerGiven = examHistoryData?.[getQuestionId()]?.answerGiven || selectedAnswer;
      if (answerGiven) {
        answeredSignedOutRef.current = questionKey;
        requestPublicAnswerCheck(answerGiven);
      }
    }

    if (isUserAcquisition && isUserLoggedIn) {
      if (qlinkSignedInHere) {
        setOpenReviewDrawer(true);
      } else if (!qlinkSameQuestionRerun) {
        // Arriving at a question signed in (unchanged): closed.
        setOpenReviewDrawer(false);
      }
    }

    setStrikeValues(strikedHistoryData[questionData?.questionId] || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData, isUserLoggedIn]);

  useEffect(() => {
    if ((isReview || isTutor || (isUserAcquisition))) {
      if (Array.isArray(questionMetadata) && questionMetadata.length) {
        const currentQues = questionMetadata.find((item) => isCurrentQuestionMetadata(item));
        setQuesMetadata(currentQues || {});
      } else {
        setQuesMetadata({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionMetadata, questionData]);

  useEffect(() => {
    if (isReview && quizHistoryData && quizHistoryData.length) {
      const currentQues = quizHistoryData.filter(
        (item) => item.questionId === questionData?.questionId
      );
      setQuizHistory(currentQues[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizHistoryData, questionData, currentQuestionNum]);

  useEffect(() => {
    const answerGiven = examHistoryData?.[getQuestionId()]?.answerGiven || selectedAnswer;
    if (answerGiven) {
      safeRecordQLinkQuestionAnswered(answerGiven);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, userDetailsFromTokenRes, quesMetadata]);

  useEffect(() => {
    if (drawerRef.current) {
      drawerRef.current.scrollTop = 0;
    }
  }, [currentQuestionNum]);

  // Slice-B rehydration: a refreshed (or back-nav'd) tutor quiz restores its
  // answers from question-history but holds no in-session grade. The A2
  // endpoint's finality path returns the already-recorded result on a
  // re-POST, so fetch the grade once for an answered question — no-op when a
  // grade is already held or a POST is in flight.
  useEffect(() => {
    if (!serverGradingActive) {
      return;
    }

    const answerGiven = examHistoryData?.[getQuestionId()]?.answerGiven;
    if (answerGiven) {
      submitAnswerForServerGrade(answerGiven);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData, serverGradingActive]);

  const handleChange = (event, selectedValue = '') => {
    if (selectedValue && !!isReview) {
      return;
    }

    // Finality lock (#528 port, gate-verified): once a grade request is in
    // flight or a grade is held, the answer is (about to be) FINAL on the
    // server — a re-pick, including a double-click racing the response, must
    // not rewrite the locally recorded answerGiven or move the radio, or the
    // UI diverges from the server-recorded final answer. An errored request
    // releases the pending lock (fail-open), so answering still works after
    // a failure. Flag off, serverGradingActive is false and this never runs.
    const gradeLockKey = normalizeQuestionId(getQuestionId());
    if (
      serverGradingActive &&
      (serverGrades[gradeLockKey] || pendingServerGradeRef.current.has(gradeLockKey))
    ) {
      return;
    }

    const val = event ? event.target.value : selectedValue;
    setSelectedAnswer(val);
    setAnsweredByArrowKey(lastAnswerInputRef.current === "arrow");

    if (isTutor || isUserAcquisition) {
      drawerAutoOpenedRef.current = true; // opened BY the answer, not by the student
      setOpenReviewDrawer(true);
    }

    if (isUserAcquisition && !isUserLoggedIn) {
      answeredSignedOutRef.current = normalizeQuestionId(getQuestionId());
    }


    if (!examHistoryData[questionData?.questionId || questionData?.id]) {
      if (!isUserAcquisition && !isTutor && currentQuestionNum === dataLength) {
        onShowEndBlock(true);
      }
      examHistoryData[questionData?.questionId || questionData?.id] = {};
    }

    if (examHistoryData[questionData?.questionId || questionData?.id]) {
      examHistoryData[questionData?.questionId || questionData?.id].answerGiven = val;
    }

    onExamHistoryChange(examHistoryData);
    safeRecordQLinkQuestionAnswered(val);
    submitAnswerForServerGrade(val);
    requestPublicAnswerCheck(val);
  };

  const proceedToNextQuestion = () => {
    onProceedToNextQuestion();
  };

  const onOpenReviewDrawerToggle = () => {
    // A deliberate open. This is the ONLY site that sets the flag false, and it
    // is why the flag has to live here rather than be derived from mode:
    // isAnswered implies isReview or isTutor, so a mode-derived "autoOpened" was
    // true by construction and its complement was unreachable — the dimension
    // separated nothing.
    if (!openReviewDrawer) {
      drawerAutoOpenedRef.current = false;
      setAnsweredByArrowKey(false); // deliberate open: the teaser may take focus
    }
    setOpenReviewDrawer(!openReviewDrawer);
  };

  // Arrow keys check a radio as they move, so an arrow-key answer must not pull
  // focus out of the option group (WCAG 3.2.2); the verdict is announced instead.
  const lastAnswerInputRef = useRef(null);
  const [answeredByArrowKey, setAnsweredByArrowKey] = useState(false);
  const onAnswerKeyDown = (event) => {
    lastAnswerInputRef.current = /^Arrow/.test(event.key) ? "arrow" : "other";
  };
  const onAnswerPointerDown = () => {
    lastAnswerInputRef.current = "pointer";
  };

  // "‹ Question" in the teaser sheet: back to the stem, focus on the labelled
  // re-entry so the wall is never lost.
  const teaserReentryRef = useRef(null);
  const teaserFocusReentryRef = useRef(false);
  const onTeaserBackToQuestion = () => {
    teaserFocusReentryRef.current = true;
    setOpenReviewDrawer(false);
  };
  const onTeaserReopen = () => {
    drawerAutoOpenedRef.current = false; // a deliberate reopen
    // Focus suppression is only for the AUTOMATIC open after an arrow-key
    // answer; a deliberate reopen takes focus to the verdict again.
    setAnsweredByArrowKey(false);
    setOpenReviewDrawer(true);
  };
  useEffect(() => {
    if (!openReviewDrawer && teaserFocusReentryRef.current && teaserReentryRef.current) {
      teaserFocusReentryRef.current = false;
      teaserReentryRef.current.focus();
    }
  }, [openReviewDrawer]);

  const handleContainerClick = (e) => {
    // interactive elements - toggle disabled
    const interactiveElements = ['INPUT', 'BUTTON', 'A', 'LABEL', 'svg', 'path'];
    const clickedElement = e.target;

    // idntify interactive elem click
    if (interactiveElements.includes(clickedElement.tagName) ||
        clickedElement.closest('button') ||
        clickedElement.closest('label') ||
        clickedElement.closest('a') ||
        clickedElement.closest('.answer-wrapper') ||
        clickedElement.closest('.drawer-close-btn') ||
        clickedElement.closest('.proceed-btn') ||
        clickedElement.closest('.ques-img-cont') ||
        clickedElement.closest('iframe')) {
      return;
    }

    onOpenReviewDrawerToggle();
  };

  const onHideAnswerToggle = () => {
    setHideAnswer(!hideAnswer);
  };

  const onStrikeAnswerToggle = (index) => {
    const arr = strikedHistoryData[questionData.questionId] || [...strikeValues];
    const valIndex = arr.indexOf(index);

    if (valIndex === -1) {
      arr.push(index);
    } else {
      arr.splice(valIndex, 1);
    }

    const updatedData = {
      ...strikedHistoryData,
      [questionData.questionId]: arr
    }

    onStrikeDataChange(updatedData);
    setStrikeValues([...arr]);
  };

  const onImageZoom = (isZoomIn) => {
    const imgHeight = imgRef.current.clientHeight;

    if ((!isZoomIn && imgHeight < CONSTANTS.minImageZoom) || (isZoomIn && imgHeight > CONSTANTS.maxImageZoom)) {
      return;
    }

    if (isZoomIn) {
      imgRef.current.style.height = (imgHeight + 100) + "px";
    } else {
      imgRef.current.style.height = (imgHeight - 100) + "px";
    }
  };

  // const onOpenPdf = () => {
  //   setOpenPdf(true);
  //   setPdfData(pdfQuesData[(currentQuestionNum - 1) % 2]);
  // };

  const setQuesHighlight = (value) => {
    const localVal = JSON.parse(JSON.stringify(highlightValues));
    localVal[questionData?.questionId] = value;
    setHighlightValues(localVal);
  };


  // What the signed-out drawer shows: correctness is recomputed from the known
  // key for the CURRENT pick, so a re-pick after the check needs no second call.
  const buildPublicAnswerResult = () => {
    if (!currentPublicCheck) {
      return undefined;
    }
    if (currentPublicCheck.status !== "done") {
      return { status: currentPublicCheck.status };
    }
    const key = currentPublicCheck.correctAnswer;
    const normalizedKey = key.trim().toLowerCase();
    const keyIndex = (questionData?.option?.optionList || []).findIndex(
      (item) => `${item || ""}`.trim().toLowerCase() === normalizedKey
    );
    const optionList = questionData?.option?.optionList || [];
    const normalizedPick = `${selectedAnswer || ""}`.trim().toLowerCase();
    const pickIndex = optionList.findIndex((item) => `${item || ""}`.trim().toLowerCase() === normalizedPick);
    const teaser = currentPublicCheck.teaser;
    // The hook is written for the option that was checked. After a re-pick the
    // verdict is recomputed locally, so drop the hook rather than show the
    // question for an option the visitor no longer holds.
    const samePick = `${currentPublicCheck.checkedAnswer || ""}`.trim().toLowerCase() === normalizedPick;
    const result = {
      status: "done",
      correct: normalizedPick === normalizedKey,
      correctAnswer: key,
      correctLetter: keyIndex >= 0 ? String.fromCodePoint(65 + keyIndex) : "",
      chosenLetter: pickIndex >= 0 ? String.fromCodePoint(65 + pickIndex) : "",
    };
    if (!teaser) {
      return result; // no curated teaser: the card shows its plain sign-in prompt
    }
    return {
      ...result,
      teaser: { ...teaser, hook: samePick ? teaser.hook : null },
    };
  };

  // Branding is for signed-out strangers only: logged-in students and tutors
  // also open /question/view (post-session quizzes), and their view stays as
  // it was. The exam is named only when the question's own categories say
  // which single exam it belongs to; otherwise the copy stays generic.
  const isSignedOutStranger = !!isUserAcquisition && !isUserLoggedIn;
  const publicAnswerResult = buildPublicAnswerResult();
  // A checked answer: the signed-out result card (curated teaser, or the plain
  // sign-in prompt) becomes the full-width sheet on phones, with a labelled way
  // back in.
  const showTeaserSheet = isSignedOutStranger && publicAnswerResult?.status === "done";
  // At <=600px the teaser is a full-screen sheet: modal while open.
  const isPhoneViewport = useMediaQuery("(max-width:600px)");
  const teaserModal = showTeaserSheet && isPhoneViewport;
  // The screen is replaced on phones, so focus always follows the sheet there.
  const teaserMoveFocus = !answeredByArrowKey || teaserModal;
  const teaserAnnouncement = showTeaserSheet && answeredByArrowKey && !teaserModal
    ? `${publicAnswerResult.correct ? "Correct!" : "Not quite."} Correct answer: ${publicAnswerResult.correctLetter ? `${publicAnswerResult.correctLetter}. ` : ""}${publicAnswerResult.correctAnswer}. The explanation panel is open.`
    : "";
  useEffect(() => {
    if (!(teaserModal && openReviewDrawer) || !drawerRef.current) return undefined;
    return inertOutside(drawerRef.current);
  }, [teaserModal, openReviewDrawer]);

  const qlinkExamLabel = resolveQlinkExamLabel(
    effectiveQuesMetadata?.categories?.examType || questionData?.categories?.examType
  );

  if (!questionData) {
    return "Loading...";
  }

  const selectedAns =
    isReview && quizHistory && quizHistory.answerGiven
      ? quizHistory.answerGiven
      : selectedAnswer;
  // Review-mode "Your answer" marking (UX-sweep slice-2 QREV-2): the radio
  // fill is the only thing that ever showed the student's pick, and it hangs
  // on an EXACT string match against the stored answerGiven — when that match
  // misses, review renders with nothing marking the chosen option and the
  // mistake is unlearnable. Resolve the pick to an option INDEX once, with
  // the same trim+case normalization the correct-answer icons use; rows then
  // mark by index. A stored answer that matches NO option must never mark a
  // row (no false "Your answer") — it renders as a fallback line instead.
  const normalizedChosenAnswer = `${selectedAns || ""}`.trim().toLowerCase();
  const showReviewAnswers = !!isReview && !hideAnswer;
  // The signed-out visitor's own pick is marked too once the teaser is showing,
  // so "‹ Question" lets them re-read the stem against their choice.
  const showOwnPick = showReviewAnswers || showTeaserSheet;
  const chosenOptionIndex =
    showOwnPick && normalizedChosenAnswer
      ? (questionData?.option?.optionList || []).findIndex(
          (item) => `${item || ""}`.trim().toLowerCase() === normalizedChosenAnswer
        )
      : -1;
  const chosenAnswerUnmatched =
    showReviewAnswers && !!normalizedChosenAnswer && chosenOptionIndex === -1;
  // The CURRENT question's recorded answer (examHistoryData is keyed by
  // questionId and written synchronously by handleChange; selectedAnswer lags
  // a question change by one render).
  const proceedPinned = !isReview && !!examHistoryData?.[getQuestionId()]?.answerGiven;
  const hasQuestionMetadataBatch = Array.isArray(questionMetadata) && questionMetadata.length > 0;
  const hasCurrentQuestionMetadata = isCurrentQuestionMetadata(quesMetadata);
  const shouldWaitForQuestionMetadata = questionMetadataLoading || (!questionMetadataLoaded && !hasQuestionMetadataBatch);
  const isQuestionMetadataLoading =
    (isReview || isTutor || isUserAcquisition) &&
    !!getQuestionId() &&
    !questionMetadataError &&
    !hasCurrentQuestionMetadata &&
    shouldWaitForQuestionMetadata;

  return (
    <Box component="div" className="container" onClick={handleContainerClick}>
      {/* review-mode class scopes the mobile bottom-sheet treatment (QREV-3)
          to REVIEW only — tutor/user-acquisition keep the side drawer, whose
          absolutely-positioned login boxes depend on that geometry. */}
      {openReviewDrawer &&
        <Box className={`drawer-overlay${isReview ? " review-mode" : ""}`} onClick={onOpenReviewDrawerToggle}></Box>
      }

      {/* data-hj-suppress: Hotjar replays the DOM, so without this a session
          recording carries the question stem and every option verbatim —
          licensed bank content, and (with GL) a student's own reasoning. The
          attribute is INHERITED by descendants and, unlike the GL route guard
          in public/index.html, it survives SPA client-nav onto this surface
          (the residual (b) that gl-tag-guard.js documents as uncovered).
          Clicks, scroll depth and rage-clicks still record — we lose the text,
          not the UX signal. */}
      {!isGuide &&
        <Box component="div" m={1} className="question-wrapper" {...REPLAY_MASK}>
          {/* <Box component="div" m={1} className="question-div" onClick={onOpenPdf}> */}
          {isSignedOutStranger && (
            <>
              <Box component="div" className="qlink-brand-header">
                {qlinkExamLabel ? `QLearn · ${qlinkExamLabel} practice question` : "QLearn practice question"}
              </Box>
              <Box component="p" className="qlink-steps">
                Answer first → see your result → sign in free for the explanation.
              </Box>
            </>
          )}
          <Box component="div" m={1} className={isTutorRole ? 'user-tutor question-div content-body' : 'question-div content-body'}>
            <Box className="watermark"></Box>
            {isTutorRole &&
              <TokenAnnotator
                tokens={questionData["stem"]?.split(' ')}
                value={highlightValues[questionData?.questionId] || []}
                onChange={value => setQuesHighlight(value)}
                getSpan={span => ({
                  ...span,
                  color: '#f3f311'
                })}
              />
            }
            {!isTutorRole &&
              <>{questionData["stem"]}</>
            }
          </Box>
          {imageData?.length > 0 &&
            <Box onClick={() => setOpenImageModal(true)} className="ques-img-cont">
              <img src={imageData} alt="" className="img-cont" />
            </Box>
          }
          <Box component="div" className="answer-container">
            <FormControl
              component="fieldset"
              className="answer-box"
              onKeyDown={onAnswerKeyDown}
              onPointerDown={onAnswerPointerDown}
            >
              <RadioGroup
                name="answer"
                // QLink page: always controlled. The group can first mount while
                // the answer is undefined, which left it uncontrolled, so a pick
                // made by tapping the option text never filled its radio.
                value={isUserAcquisition ? `${selectedAns || ""}`.trim() : selectedAns?.trim()}
                onChange={(evt) => handleChange(evt)}
              >
                {questionData?.option?.optionList?.map((item, index) => {
                  const isChosenOption = index === chosenOptionIndex;
                  return (
                    <Box
                      component="div"
                      className={`answer-wrapper${isChosenOption ? " your-answer" : ""}`}
                      key={index}
                    >
                      {/* correctAnswer is absent for ANONYMOUS QLink visitors
                          (the service strips it — content-integrity P1);
                          without this guard every option would render the
                          incorrect-X. Logged-in callers still receive it.
                          Under server grading (Slice B) effectiveQuesMetadata
                          is the server-REVEALED question, so the same guard
                          covers the pending-response window fail-closed. */}
                      {((isReview && !hideAnswer) || (isTutor && selectedAnswer) ||
                        (isUserAcquisition && (selectedAnswer))) &&
                        effectiveQuesMetadata?.option?.correctAnswer && (
                          <>
                            {effectiveQuesMetadata?.option?.correctAnswer?.toLowerCase() === item?.toLowerCase() && (
                              <Check className="correct-icon"></Check>
                            )}
                            {effectiveQuesMetadata?.option?.correctAnswer?.toLowerCase() !== item?.toLowerCase() && (
                              <Clear className="incorrect-icon"></Clear>
                            )}
                          </>
                        )}

                      {!hideAnswer &&
                        <FormControlLabel
                          className="ans-label-rad"
                          value={item.trim()}
                          disabled={!!isReview}
                          control={<Radio color="primary" />}
                        />
                      }
                      <Box component="span" className="ans-char">{String.fromCharCode(65 + (index))}. &nbsp;</Box>
                      <Box onClick={() => handleChange(null, item)}
                        className={`ans-label ${strikeValues.indexOf(index) > -1 ? "strike-ans" : null} ${!!isReview ? null : 'ans-pointer'} `}
                        onContextMenu={() => onStrikeAnswerToggle(index)}>{item}</Box>
                      {isChosenOption && (
                        <Box component="span" className="your-answer-chip">Your answer</Box>
                      )}
                    </Box>
                  );
                })}
              </RadioGroup>
              {chosenAnswerUnmatched && (
                <Box component="div" className="your-answer-fallback">
                  Your answer: {`${selectedAns || ""}`.trim()}
                </Box>
              )}
              {isReview &&
                <Tooltip title={`${hideAnswer ? 'Show Answers' : 'Hide Answers'}`}>
                  <Visibility onClick={onHideAnswerToggle}
                    className={`eye-icon ${hideAnswer ? 'selected' : 'disabled'}`}></Visibility>
                </Tooltip>
              }
            </FormControl>
          </Box>
          {/* proceed-bar--pinned: once this question is answered, phones keep
              the button in view in a bar just above the End Block footer
              (exam-question-container.scss). Not before an answer, where it
              would cover options still to be read, and not in review, where
              the guidance sheet's handle sits in that place. A child of the
              question, not of the options, so it stays pinned when the stem is
              scrolled back into view. */}
          {currentQuestionNum !== dataLength &&
            <Box component="div" className={`proceed-bar${proceedPinned ? " proceed-bar--pinned" : ""}`}>
              <Button
                variant="contained"
                color="primary"
                className="proceed-btn"
                onClick={proceedToNextQuestion}
              >
                Proceed to Next Question
              </Button>
            </Box>
          }
          {/* Flag-gated dark GL WHY-capture + hint affordance — renders null
              unless REACT_APP_GL_ENABLED + _AUDIENCE are on (GL-1 §0/§1/§3).
              Additive: with GL off this is a no-op and the container is
              byte-identical. Self-suppresses in review. KEYED by questionId so a
              new question is a fresh instance (no stale WHY/phase/hint carried
              into a committed render). Wired to the real GL transport + a
              userIdHash-keyed event envelope. */}
          <GlWhyCapture
            key={getQuestionId()}
            questionId={getQuestionId()}
            quizId={quizId}
            platform="web"
            isReview={!!isReview}
            userIdHash={getUserIdHash()}
            glApi={glProdApi}
            /* Card-draft provenance (wire-contract row 2): once the server
               grade lands, its gradeEventId (the question_history id) lets the
               component fire the draft call with verifiable lineage. No server
               grade => no draft, by design. */
            gradeInfo={
              currentServerGrade?.gradeEventId
                ? { gradeEventId: currentServerGrade.gradeEventId, correct: currentServerGrade.correct }
                : null
            }
            /* PER-CURRENT-QUESTION only — `selectedAnswer` is container-level
               state that lags a question change (reset by a passive effect after
               render), so including it marked a freshly-keyed next question
               committed on its first render (premature skip). `examHistoryData`
               is keyed by questionId and set synchronously in handleChange, so it
               reflects the CURRENT question's commit without carryover. */
            answerCommitted={!!examHistoryData?.[getQuestionId()]?.answerGiven}
          />
          {/* The raw bank id means nothing to a stranger on a shared link. */}
          {!isSignedOutStranger && <Box className="ques-id">{questionData.questionId}</Box>}
          {fiducialActive && (
            <SessionFiducial
              questionId={getQuestionId()}
              idBook={sessionIdBookRef.current}
            />
          )}
        </Box>
      }
      {(isUserAcquisition || isReview || ((selectedAnswer || hasAnswered.length > 0) && (isTutor || isUserAcquisition))) && (
        <Box
          component="div"
          className={`drawer ${openReviewDrawer ? "selected" : "dismiss"}${isReview ? " review-mode" : ""}${isUserAcquisition ? " acquisition-drawer" : ""}${showTeaserSheet ? " qlink-teaser-sheet" : ""}`}
          ref={drawerRef}
        >
          <Box component="div" onClick={onOpenReviewDrawerToggle} className="drawer-close-btn">
            <ArrowBackIos />
            {/* Hidden on desktop; the mobile bottom-sheet handle shows it so
                the toggle is no longer an unlabeled floating chevron. */}
            <Box component="span" className="drawer-toggle-lbl">
              {openReviewDrawer ? "Close guidance" : "Detailed Guidance"}
            </Box>
          </Box>
          <ExamReviewDrawer
            readOnly={readOnly}
            questionData={effectiveQuesMetadata}
            currentQuestionNum={currentQuestionNum}
            quizHistory={quizHistory}
            openDrawer={openReviewDrawer}
            drawerAutoOpened={drawerAutoOpenedRef.current}
            answeredCorrectly={resolveAnsweredCorrectly()}
            isOpenPdf={openPdf}
            pdfData={pdfData}
            isTutor={isTutor}
            isReview={isReview}
            isUserAcquisition={isUserAcquisition}
            isUserLoggedIn={isUserLoggedIn}
            onPdfClose={() => setOpenPdf(false)}
            onFeedbackOpen={() => onFeedbackOpen(true)}
            onGuideOpen={() => setOpenReviewDrawer(false)}
            selectedAnswer={selectedAnswer}
            handleFetchQuestionMetadata={handleFetchQuestionMetadata}
            questionMetadataError={questionMetadataError}
            isQuestionMetadataLoading={isQuestionMetadataLoading}
            serverGradeError={currentServerGradeError}
            isAnswered={isAnswered}
            hasAnswered={hasAnsweredLocally}
            onRetryExplanation={retryExplanation}
            qlinkId={qlinkId}
            qlinkPlatform={qlinkPlatform}
            publicAnswerResult={publicAnswerResult}
            qlinkExamLabel={qlinkExamLabel}
            publicQuestionId={isSignedOutStranger ? normalizeQuestionId(getQuestionId()) || undefined : undefined}
            onBackToQuestion={onTeaserBackToQuestion}
            teaserModal={teaserModal}
            teaserMoveFocus={teaserMoveFocus}
          ></ExamReviewDrawer>
        </Box>
      )}
      {isSignedOutStranger && (
        <div className="qlt-sr-only" role="status" aria-live="polite">{teaserAnnouncement}</div>
      )}
      {showTeaserSheet && !openReviewDrawer && (
        <button
          type="button"
          className="qlink-teaser-reentry"
          ref={teaserReentryRef}
          onClick={onTeaserReopen}
        >
          See why · free account
        </button>
      )}

      {/* Flag-gated dark GL-4b card review drawer — renders null unless GL
          flag+audience are on AND the question is graded (post-answer). Additive:
          with GL off this is a no-op and the container is byte-identical. */}
      <GlCardReviewDrawer
        questionId={getQuestionId()}
        graded={!!selectedAnswer || !!examHistoryData?.[getQuestionId()]?.answerGiven}
      />

      {/* Flag-gated dark "More Like This" — renders null unless the feature flag is on. */}
      <MoreLikeThisPanel seedQuestionId={getQuestionId()} show={!!isReview && !readOnly} />

      {/* Flag-gated dark Audio Mode "Listen" — renders null unless flagged on AND the
          question has a manifest; answer-revealing segments only in review. */}
      {!readOnly && <AudioListenPanel questionId={getQuestionId()} isReview={!!isReview} />}

      <Modal
        className='image-enlarge-modal'
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={openImageModal}>
          {/* The enlarged question image renders in a portal, outside the
              masked question-wrapper above: masked here too (review of #866). */}
          <Box className='modal-container' {...REPLAY_MASK}>
            <img src={imageData} alt="Qlearn" ref={imgRef} className="img-cont" />
            <Box className="zoom-btn-container">
              <IconButton className='zoom-btn' size="medium" title="Zoom Out" onClick={() => onImageZoom(false)}>
                <ZoomOut fontSize="medium" />
              </IconButton>
              <IconButton className='zoom-btn' size="medium" title="Zoom In" onClick={() => onImageZoom(true)}>
                <ZoomIn fontSize="medium" />
              </IconButton>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ExamQuestionContainer;
